import React, { useContext, useState, useRef } from "react";
import XLSX from "sheetjs-style";
import { useSelector } from "react-redux";
import axios from "axios";
import { getSessionStorage } from "../../Helper";
import UserContext from "../../components/Context/UserContext";
import { API_BASE_URL } from "../../Config";
import Swal from "sweetalert2";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material UI components
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

// Icons
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ErrorIcon from "@mui/icons-material/Error";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { Card } from "@mui/material";

const MisReport = () => {
  // Safely destructure context with default values
  const context = useContext(UserContext) || {};
  const { setLoading1 = () => {}, loading1 = false } = context;

  const showSidebar = useSelector((state) => state.sidebar.showSidebar);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [errorModal, setErrorModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const currentDate = new Date().toISOString().split("T")[0];
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  const oneMonthAgoDate = oneMonthAgo.toISOString().split("T")[0];
  const [maxToDate, setMaxToDate] = useState("");
  const toDateInputRef = useRef(null);

  const handleFromDateChange = (e) => {
    const selectedDate = e.target.value;
    setFromDate(selectedDate);

    const date = new Date(selectedDate);
    date.setDate(date.getDate() + 30);
    const maxDate = date.toISOString().split("T")[0];
    setMaxToDate(maxDate);
    setToDate("");
  };

  const handleToDateClick = () => {
    if (fromDate && !toDate) {
      toDateInputRef.current.value = fromDate;
      setTimeout(() => {
        toDateInputRef.current.value = "";
      }, 0);
    }
  };

  const handleToDateChange = (e) => {
    const selectedDate = e.target.value;
    if (selectedDate >= fromDate && selectedDate <= maxToDate) {
      setToDate(selectedDate);
    } else {
      alert("Please select a date within 30 days from From Date");
      setToDate("");
    }
  };

  const handleFilterSubmit = async () => {
    if (fromDate && toDate) {
      if (fromDate <= toDate) {
        setLoading(true);
        setLoading1(true);
        try {
          const obj = {
            accode: getSessionStorage("accode"),
            cid: getSessionStorage("cid"),
            datefrom: fromDate.split("-").reverse().join("/"),
            dateto: toDate.split("-").reverse().join("/"),
          };

          // First API call for MIS report
          const misResponse = await axios.post(`${API_BASE_URL}/Mis/report`, obj);
          // Second API call for report details
          const detailsResponse = await axios.post(`${API_BASE_URL}/Mis/reportdetails`, obj);

          // Initialize totals for Grand Total row
          let totalDeliveriesSum = 0;
          let deliveredSum = 0;
          let internalDelaySum = 0;
          let externalDelaySum = 0;

          // Combine both API responses and add delivery percentage
          const combinedData = misResponse.data.map((item) => {
            // Calculate delivery percentage
            const deliveryPercentage = (item.delivered / item.totalDel) * 100 || 0;

            // Accumulate the sums for Grand Total
            totalDeliveriesSum += item.totalDel || 0;
            deliveredSum += item.delivered || 0;
            internalDelaySum += item.delayInternal || 0;
            externalDelaySum += item.delayExternal || 0;

            return {
              "Branch Name": item.branchName || "",
              Delivered: item.delivered || 0,
              "Internal Delay": item.delayInternal || 0,
              "External Delay": item.delayExternal || 0,
              "Total Deliveries": item.totalDel || 0,
              "Delivery %": deliveryPercentage.toFixed(2) + "%" || "0%",
            };
          });

          // Add the grand total row
          const grandTotalPercentage = (deliveredSum / totalDeliveriesSum) * 100 || 0;
          combinedData.push({
            "Branch Name": "Grand Total",
            Delivered: deliveredSum,
            "Internal Delay": internalDelaySum,
            "External Delay": externalDelaySum,
            "Total Deliveries": totalDeliveriesSum,
            "Delivery %": grandTotalPercentage.toFixed(2) + "%" || "0%",
          });

          // Process details response (same as before)
          const detailData = detailsResponse.data.map((item) => ({
            "C Note No": item.cNoteNo || "",
            "C Note Date": item.cNoteDate || "",
            "Entry Date": (item.createDate || "").split(" ")[0],
            "Pickup Pincode": item.fromPlace || "",
            Origin: item.origin || "",
            "Consignor Name": item.consignor || "",
            "Consignee Name": item.consignee || "",
            "Delivery Pincode": item.toPlace || "",
            Destination: item.destination || "",
            "Current Location": item.currentLocation || "",
            "Invoice No": item.invoiceNo || "",
            "Invoice Amount": item.invAmount || "",
            "Total Box": item.noPackages || "",
            "Actual Weight": item.actWeight || "",
            "Charged Weight": item.chargeableWeight || "",
            "Total Freight": item.subTotal || "",
            "Transit Type": item.transitName || "",
            "TAT Days": item.transitDays || "",
            CPM: item.cpmDate || "",
            ADD: item.addDate || "",
            Status: item.currentStatus || "",
            "Delivery Status": item.drsStatus || "",
            "POD Status": item.podStatus || "",
            "Delivery Performance": item.perform || "",
            "Hold Type": item.branchRemark1 || "",
            "Hold Reason": item.branchRemark2 || "",
            "Eway Bill No - Date": item.ewayBillDate || "",
            "Asn No": item.compRefNo || "",
            "Vendor Code": item.asnCode || "",
            "Dc Code": item.dcCode || "",
            "Tripsheet No": item.refSheetNo || "",
            "Ref Doc No": item.refDocNo || "",
            "STN No": item.stnNo || "",
            "Arrive Date": item.arrivalDate || "",
            "Branch In": item.prsDate || "",
            Volumet: item.volumeTs || "",
          }));

          // Create a worksheet for each report
          const misWorksheet = XLSX.utils.json_to_sheet(combinedData);
          const detailsWorksheet = XLSX.utils.json_to_sheet(detailData);

          //set width for every columns
          detailsWorksheet["!cols"] = [
            { wch: 10 }, // "C Note No" column
            { wch: 13 }, // "C Note Date" column
            { wch: 13 }, // "Entry Date" column
            { wch: 20 }, // "Pickup Pincode" column
            { wch: 15 }, // "Origin" column
            { wch: 30 }, // "Consignor Name" column
            { wch: 30 }, // "Consignee Name" column
            { wch: 10 }, // "Delivery Pincode" column
            { wch: 10 }, // "Destination" column
            { wch: 20 }, // "Current Location" column
            { wch: 25 }, // "Invoice No" column
            { wch: 8 }, // "Invoice Amount" column
            { wch: 3 }, // "Total Box" column
            { wch: 4 }, // "Actual Weight" column
            { wch: 5 }, // "Charged Weight" column
            { wch: 5 }, // "Total Freight" column
            { wch: 5 }, // "Transit Type" column
            { wch: 5 }, // "TAT Days" column
            { wch: 10 }, // "CPM" column
            { wch: 10 }, // "ADD" column
            { wch: 10 }, // "Status" column
            { wch: 10 }, // "Delivery Status" column
            { wch: 10 }, // "POD Status" column
            { wch: 10 }, // "Delivery Performance" column
            { wch: 10 }, // "Hold Type" column
            { wch: 25 }, // "Hold Reason" column
            { wch: 10 }, // "Eway Bill No - Date" column
            { wch: 5 }, // "Asn No" column
            { wch: 10 }, // "Vendor Code" column
            { wch: 10 }, // "Dc Code" column
            { wch: 10 }, // "Tripsheet No" column
            { wch: 10 }, // "Ref Doc No" column
            { wch: 10 }, // "STN No" column
            { wch: 10 }, // "Arrive Date" column
            { wch: 15 }, // "Branch In" column
            { wch: 15 }, // "Volumet" column
          ];

          // Style the header row
          const styleHeader = (worksheet) => {
            const range = XLSX.utils.decode_range(worksheet["!ref"]);
            for (let C = range.s.c; C <= range.e.c; ++C) {
              const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
              if (!worksheet[cellAddress]) continue;
              worksheet[cellAddress].s = {
                font: { bold: true },
              };
            }
          };

          // Style the Grand Total row (last row)
          const styleGrandTotal = (worksheet) => {
            const range = XLSX.utils.decode_range(worksheet["!ref"]);
            const lastRowIndex = range.e.r; // Last row is the Grand Total row

            for (let C = range.s.c; C <= range.e.c; ++C) {
              const cellAddress = XLSX.utils.encode_cell({ r: lastRowIndex, c: C });
              if (!worksheet[cellAddress]) continue;
              worksheet[cellAddress].s = {
                font: { bold: true }, // Bold font for grand total row
              };
            }
          };

          styleHeader(misWorksheet);
          styleGrandTotal(misWorksheet);
          styleHeader(detailsWorksheet);

          // Apply styles
          const range = XLSX.utils.decode_range(misWorksheet["!ref"]);
          for (let R = range.s.r; R <= range.e.r; ++R) {
            for (let C = range.s.c; C <= range.e.c; ++C) {
              const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
              const cell = misWorksheet[cellAddress] || {};

              // Apply bold style for the header row and Grand Total row
              if (R === range.e.r) {
                cell.s = {
                  font: { bold: true },
                  alignment: { horizontal: "right" }, // Center the header text
                };
              }

              // Apply right alignment to "Delivery %" column (index 3)
              if (C === 3) {
                cell.s = {
                  ...(cell.s || {}),
                  alignment: { horizontal: "right" }, // Right-align the delivery %
                };
              }

              misWorksheet[cellAddress] = cell;
            }
          }

          // Create a new workbook and append both worksheets
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, misWorksheet, "MIS Report");
          XLSX.utils.book_append_sheet(workbook, detailsWorksheet, "Report Details");

          // Generate the Excel file
          const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
          const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "MisReport.xlsx";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          setLoading(false);
          setLoading1(false);
        } catch (error) {
          console.error("Error occurred while fetching data", error);
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: "Error occurred while connecting to the database!",
            timer: 930,
            showConfirmButton: false,
          });
          setLoading(false);
          setLoading1(false);
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Invalid Date Range",
          text: "From date should be less than or equal to the To date.",
          timer: 930,
          showConfirmButton: false,
        });
      }
    } else {
      Swal.fire({
        icon: "error",
        title: "Missing Dates",
        text: "Please select both from and to dates.",
        timer: 930,
        showConfirmButton: false,
      });
    }
  };

  // Modal style
  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    outline: "none",
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox
        className={`mt-[24%] sm:mt-10 md:mt-0 lg:mt-0 2xl:mt-0 h-[80%] ${
          showSidebar
            ? "transition-all transition-slowest ease ml-20 lg:ml-[18%]"
            : "transition-all transition-slowest ease"
        }`}
      >
        <Card sx={{ p: 2, boxShadow: 3, mt: 3 }}>
          <MDBox
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            // ml={2}
            // mr={2}
            mb={2}
            // mt={3}
            // px={6}
            // py={2}
            // shadow="lg"
            // borderRadius="lg"
            // bgColor="white"
          >
            <MDBox width={{ xs: "100%", sm: "30%", lg: "20%" }} mt={2} mb={2} px={2}>
              <MDTypography variant="caption" fontWeight="medium" color="text">
                From Date <span style={{ color: "red" }}>*</span>
              </MDTypography>
              <MDInput
                type="date"
                fullWidth
                value={fromDate}
                onChange={handleFromDateChange}
                max={currentDate}
              />
            </MDBox>

            <MDBox width={{ xs: "100%", sm: "30%", lg: "20%" }} mt={2} mb={2} px={2}>
              <MDTypography variant="caption" fontWeight="medium" color="text">
                To Date <span style={{ color: "red" }}>*</span>
              </MDTypography>
              <MDInput
                type="date"
                inputRef={toDateInputRef}
                value={toDate}
                onChange={handleToDateChange}
                onClick={handleToDateClick}
                min={fromDate}
                max={maxToDate}
                fullWidth
              />
            </MDBox>

            <MDBox mt={6} mb={2} px={2}>
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleFilterSubmit}
                disabled={loading}
                startIcon={
                  loading ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />
                }
              >
                {loading ? "Loading..." : "Excel"}
              </MDButton>
            </MDBox>
          </MDBox>

          {/* Error Modal */}
          <Modal open={errorModal} onClose={() => setErrorModal(false)}>
            <Box sx={modalStyle}>
              <MDTypography variant="h6" component="h2" mb={2}>
                Error
              </MDTypography>
              <MDBox display="flex" alignItems="center">
                <ErrorIcon color="error" sx={{ mr: 1 }} />
                <MDTypography>
                  Invalid date range selected. Please ensure &apos;From Date&apos; is less than or
                  equal to &apos;To Date&apos;.
                </MDTypography>
              </MDBox>
              <MDBox mt={3} display="flex" justifyContent="flex-end">
                <MDButton variant="gradient" color="info" onClick={() => setErrorModal(false)}>
                  Close
                </MDButton>
              </MDBox>
            </Box>
          </Modal>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
};

export default MisReport;
